import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchMtrDetail } from '#/api/imsMgmt/settings/mtr/fetchDetail';
import { fetchMtrUnitList } from '#/api/imsMgmt/settings/mtr/units';

import { extractUnitOptions } from './extractUnitOptions';
import { mapUnitOptionsToSelectOptions } from './mapUnitOptionsToSelectOptions';
import { materialUnitOptionsQueryKey } from './materialUnitQueryKeys';
import { pickDefaultUnitId } from './pickDefaultUnitId';
import { resolveUnitId } from './resolveUnitId';

function hasUsableUnitOptions(unitOptions) {
  return mapUnitOptionsToSelectOptions(unitOptions).length > 0;
}

function filterActiveUnitRows(rows = []) {
  return (Array.isArray(rows) ? rows : []).filter((row) => (
    row?.status === 'ACTIVE' && Boolean(resolveUnitId(row))
  ));
}

/**
 * 單據選單位：優先 initialOptions → GET /mtr/{id} 抽 unitOptions／unit；
 * extract 後若無帶 id 的 options，再 fallback GET /mtr/{id}/units（只留 ACTIVE）。
 *
 * @param {object} params
 * @param {string} [params.mtrId]
 * @param {Array<object>|null} [params.initialOptions]
 * @param {boolean} [params.enabled]
 */
export function useMaterialUnitOptions({
  mtrId = '',
  initialOptions = null,
  enabled = true,
} = {}) {
  const resolvedMtrId = String(mtrId ?? '').trim();
  const initialUsable = Array.isArray(initialOptions)
    && initialOptions.length > 0
    && hasUsableUnitOptions(initialOptions);

  const detailEnabled = Boolean(enabled && resolvedMtrId && !initialUsable);

  const detailQuery = useQuery({
    queryKey: materialUnitOptionsQueryKey(resolvedMtrId),
    queryFn: () => fetchMtrDetail(resolvedMtrId),
    enabled: detailEnabled,
  });

  const primaryOptions = useMemo(() => {
    if (initialUsable) {
      return initialOptions;
    }

    if (!detailEnabled) {
      return [];
    }

    const detail = detailQuery.data?.data;
    if (!detail) {
      return [];
    }

    if (
      resolvedMtrId
      && detail.id != null
      && String(detail.id) !== resolvedMtrId
    ) {
      return [];
    }

    return extractUnitOptions(detail);
  }, [
    initialUsable,
    initialOptions,
    detailEnabled,
    detailQuery.data,
    resolvedMtrId,
  ]);

  const primaryUsable = hasUsableUnitOptions(primaryOptions);

  const listEnabled = Boolean(
    enabled
    && resolvedMtrId
    && !primaryUsable
    && detailEnabled
    && (detailQuery.isFetched || detailQuery.isError),
  );

  const listQuery = useQuery({
    queryKey: [...materialUnitOptionsQueryKey(resolvedMtrId), 'activeList'],
    queryFn: () => fetchMtrUnitList(resolvedMtrId),
    enabled: listEnabled,
  });

  const options = useMemo(() => {
    if (primaryUsable) {
      return primaryOptions;
    }
    if (!listEnabled) {
      return primaryOptions;
    }
    return filterActiveUnitRows(listQuery.data?.data);
  }, [
    primaryUsable,
    primaryOptions,
    listEnabled,
    listQuery.data,
  ]);

  const selectOptions = useMemo(
    () => mapUnitOptionsToSelectOptions(options),
    [options],
  );

  const detailData = detailQuery.data?.data;
  const defaultDisplayUnit = detailData?.defaultDisplayUnit ?? null;
  const receiptDefaultQty = detailData?.receiptDefaultQty ?? null;

  const defaultUnitId = useMemo(
    () => pickDefaultUnitId(selectOptions, { defaultDisplayUnit }),
    [selectOptions, defaultDisplayUnit],
  );

  const isLoading = Boolean(
    (detailEnabled && detailQuery.isLoading)
    || (listEnabled && listQuery.isLoading),
  );

  const isError = Boolean(
    (detailEnabled && detailQuery.isError && !listEnabled)
    || (listEnabled && listQuery.isError),
  );

  const error = listEnabled && listQuery.isError
    ? listQuery.error
    : (detailEnabled && detailQuery.isError ? detailQuery.error : null);

  return {
    options,
    selectOptions,
    defaultUnitId,
    defaultDisplayUnit,
    receiptDefaultQty,
    isLoading,
    isError,
    error,
  };
}
